import React, { useState, useEffect } from "react";
import { Button, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import BulkInputComponent from "./BulkInputComponent";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../hooks/useTypedSelector";
import {
  bulkUpdate,
  clearSelectedIds,
  resetBulkValues,
  updateBulkValues,
} from "../../../infrastructure/slices/bulkSelectSlice/bulkSelectSlice";

interface BulkProps {
  entity: string;
  bulkFields?: {
    field: string;
    options?: { label: string; value: string }[];
    date?: boolean;
    loadOptions?: (
      input: string,
      callback: (options: { label: string; value: string }[]) => void,
    ) => void;
  }[];
  selectedIds?: string[];
  pageName: string;
  showBulkPopup: boolean;
  toggleBulkPopup: () => void;
  bulkFieldsResponseExclude?: string[];
  bulkFieldsRequiredGroup?: string[];
  bulkFieldsMessageAboveRequiredGroup?: string;
  dealerAnalysis?: {
    hasCommonDealer: boolean;
    commonDealer: { id: string; name: string } | null;
    multipleDealers: boolean;
  };
}

export const BulkActions = ({
  entity,
  bulkFields,
  selectedIds,
  pageName,
  showBulkPopup,
  toggleBulkPopup,
  bulkFieldsResponseExclude,
  bulkFieldsRequiredGroup = [],
  bulkFieldsMessageAboveRequiredGroup,
  dealerAnalysis,
}: BulkProps) => {
  const { t } = useTranslation("common");
  const dispatch = useAppDispatch();
  const bulkValues = useAppSelector((state) => state.bulkSelected.bulkValues);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Auto-populate relation field when all selected items have the same dealer
  useEffect(() => {
    if (!showBulkPopup) return; // Only run when popup is open

    if (dealerAnalysis?.hasCommonDealer && dealerAnalysis.commonDealer) {
      const existingRelationValue = bulkValues.find(
        (v) => v.field === "relation",
      );

      // Always update to the current common dealer, even if there was a previous value
      if (
        !existingRelationValue ||
        existingRelationValue.value !== dealerAnalysis.commonDealer.id
      ) {
        const updatedBulkValues = [
          ...bulkValues.filter((v) => v.field !== "relation"),
          {
            field: "relation",
            value: dealerAnalysis.commonDealer.id,
          },
        ];
        dispatch(updateBulkValues(updatedBulkValues));
      }
    } else if (
      dealerAnalysis?.multipleDealers ||
      dealerAnalysis?.hasCommonDealer === false
    ) {
      // Clear relation field when multiple dealers or no common dealer
      const existingRelationValue = bulkValues.find(
        (v) => v.field === "relation",
      );
      if (existingRelationValue && existingRelationValue.value !== "") {
        const updatedBulkValues = bulkValues.filter(
          (v) => v.field !== "relation",
        );
        dispatch(updateBulkValues(updatedBulkValues));
      }
    }
  }, [
    dealerAnalysis?.hasCommonDealer,
    dealerAnalysis?.multipleDealers,
    dealerAnalysis?.commonDealer?.id,
    dispatch,
    showBulkPopup,
  ]); // More specific dependencies

  // Clear relation field when popup opens if the selection context has changed
  useEffect(() => {
    if (showBulkPopup && dealerAnalysis && !dealerAnalysis.hasCommonDealer) {
      const existingRelationValue = bulkValues.find(
        (v) => v.field === "relation",
      );
      if (existingRelationValue && existingRelationValue.value !== "") {
        // Clear any previously auto-populated value when opening form with different selection
        const updatedBulkValues = bulkValues.filter(
          (v) => v.field !== "relation",
        );
        dispatch(updateBulkValues(updatedBulkValues));
      }
    }
  }, [showBulkPopup]); // Run when popup opens

  // Reset bulk values completely when dealer analysis changes to multiple dealers or no dealers
  useEffect(() => {
    if (dealerAnalysis && !dealerAnalysis.hasCommonDealer) {
      dispatch(resetBulkValues());
    }
  }, [
    dealerAnalysis?.hasCommonDealer,
    dealerAnalysis?.multipleDealers,
    dispatch,
  ]);

  // Remove the separate clearing effect as it's now handled in the main effect above

  const isFilled = (field: string) => {
    const found = bulkValues.find((v) => v.field === field);
    return found && found.value !== undefined && found.value !== "";
  };

  // Modified logic: relation field being auto-filled doesn't make other fields required
  // Only when state or in_out_storage are manually filled, they become a required group
  const nonRelationRequiredFields = bulkFieldsRequiredGroup.filter(
    (field) => field !== "relation",
  );
  const isRequiredGroupActive = nonRelationRequiredFields.some(isFilled);

  const validateRequiredGroup = () => {
    // If only relation is filled (auto-populated), no validation needed for other fields
    if (isFilled("relation") && !nonRelationRequiredFields.some(isFilled)) {
      return true;
    }

    // If state or in_out_storage are filled, both must be filled
    if (nonRelationRequiredFields.some(isFilled)) {
      return nonRelationRequiredFields.every(isFilled);
    }

    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setValidationError(null);
    if (!validateRequiredGroup()) {
      setValidationError(t("Please fill all required fields in the group."));
      return;
    }
    await dispatch(
      bulkUpdate({
        model: entity,
        ids: selectedIds as string[],
        fields: bulkValues,
        bulkFieldsResponseExclude,
      }),
    );
    dispatch(resetBulkValues());
    dispatch(clearSelectedIds({ page: pageName }));
    window.location.reload();
  };

  if (!bulkFields || bulkFields.length === 0) {
    return null;
  }

  return (
    <>
      {showBulkPopup && (
        <div
          className={"popup-bg"}
          onClick={toggleBulkPopup} // Close when clicking the backdrop
        >
          <form
            className={"d-flex flex-column popup-bulk"}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={handleSubmit}
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the form
          >
            <h4>{t("Bulk form")}</h4>
            {selectedIds && (
              <h6>
                {t("{{amt}} items selected", { amt: selectedIds.length })}
              </h6>
            )}
            {validationError && (
              <div style={{ color: "red", marginBottom: "10px" }}>
                {validationError}
              </div>
            )}
            {dealerAnalysis?.multipleDealers && (
              <div
                className="alert alert-warning d-flex align-items-center"
                role="alert"
              >
                <span className="me-2">⚠️</span>
                <span>{t("validation.multiple_dealers_selected")}</span>
              </div>
            )}
            <Row className={"d-flex flex-column"}>
              {bulkFields.map((field) => {
                // Show warning below relation field if multiple dealers selected
                const isRelationField = field.field === "relation";

                // Show message above relation field (regardless of required group)
                const showMessageAboveThisField =
                  isRelationField && bulkFieldsMessageAboveRequiredGroup;

                // Determine if field is required based on new logic
                let isFieldRequired = false;
                if (field.field === "relation") {
                  // relation field is never required
                  isFieldRequired = false;
                } else if (nonRelationRequiredFields.includes(field.field)) {
                  // state and in_out_storage are only required if the group is active
                  isFieldRequired = isRequiredGroupActive;
                } else {
                  // other fields follow original logic
                  isFieldRequired =
                    bulkFieldsRequiredGroup.includes(field.field) &&
                    isRequiredGroupActive;
                }

                return (
                  <React.Fragment key={field.field}>
                    {showMessageAboveThisField && (
                      <span className={"mt-3 small text-primary"}>
                        {bulkFieldsMessageAboveRequiredGroup}
                      </span>
                    )}
                    <BulkInputComponent
                      field={field}
                      bulkValues={bulkValues}
                      required={isFieldRequired}
                      {...(field.field === "relation"
                        ? {
                            relationAsyncOptions: field.options,
                            relationAsyncLoader: field.loadOptions,
                          }
                        : {})}
                    />
                  </React.Fragment>
                );
              })}
            </Row>
            <div className={"d-flex flex-row justify-content-end mt-3"}>
              <Button
                size={"sm"}
                variant={"light"}
                style={{ marginRight: "10px" }}
                onClick={toggleBulkPopup}
              >
                {t("Back")}
              </Button>
              <Button size={"sm"} type="submit">
                {t("Submit")}
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};
